import { useMutation } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

export enum ValidationMessageLevel {
  ERROR = 'ERROR',
  MESSAGE = 'MESSAGE',
  WARNING = 'WARNING',
}

export type DonorMismatchData = {
  existingDonorId?: string | null;
  existingRegistrationNumber?: string | null;
  existingPedigreeName?: string | null;
  existingBirthDate?: string | null;
  inputRegistrationNumber?: string | null;
  inputPedigreeName?: string | null;
  inputBirthDate?: string | null;
};

export type ValidationFieldMessage = {
  field: string;
  message: string;
};

export type ValidationInputMessage = {
  index: number;
  field: string;
  message: string;
  level: ValidationMessageLevel;
  suggestions?: string[] | null;
  correctedValue?: string | null;
  mismatchData?: DonorMismatchData | null;
};

export type BulkDonorScheduleValidationResponse = {
  fields: ValidationFieldMessage[];
  inputs: ValidationInputMessage[];
};

export type ValidateBulkDonorScheduleRequest = {
  aspirationDate?: string | null;
  aspirationSiteId?: string | null;
  fertilizationSiteId?: string | null;
  type?: string | null;
  inputs: (BulkDonorScheduleInputRow | null)[];
};

export type UpsertBulkDonorScheduleRequest = {
  aspirationDate: string;
  aspirationSiteId: string;
  fertilizationSiteId: string;
  inputs: BulkDonorScheduleInputRow[];
};

export type BulkDonorScheduleInputRow = {
  accountCode: string;
  billAccountCode?: string | null;
  donorBreed: string;
  donorNameToMatch: string;
  donorRegistrationNumber?: string | null;
  pedigreeName?: string | null;
  pregnant?: boolean | null;
  birthDate?: string | null;
  programName?: string | null;
  tpi?: number | null;
  nm?: number | null;
  opuProgram?: string | null;
  tattoo?: string | null;
  received?: string | null;
  yardCode?: string | null;
  sireNameToMatch?: string | null;
  sireBreed?: string | null;
  sireRegistrationNumber?: string | null;
  collectionCode?: string | null;
  semenType?: string | null;
  donorLocation?: string | null;
  scheduleCode?: string | null;
  pool?: boolean | null;
  aspirationComment?: string | null;
  naab?: string | null;
  cane?: string | null;
  ship?: string | null;
  shipmentGroup?: string | null;
  freeze?: string | null;
  freezeMethod?: string | null;
  export?: string | null;
  trypsinTreat?: boolean | null;
  nonEuCountry?: string | null;
};

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export const useValidateBulkDonorSchedules = () => {
  return useMutation({
    async mutationFn(request: ValidateBulkDonorScheduleRequest) {
      const response = await fetch(
        `${getBaseUrl()}/api/v1/BulkDonorSchedule/validate`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(),
          body: JSON.stringify(request),
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as BulkDonorScheduleValidationResponse;
    },
  });
};

export const useUpsertBulkDonorSchedules = () => {
  return useMutation({
    async mutationFn(request: UpsertBulkDonorScheduleRequest) {
      const response = await fetch(
        `${getBaseUrl()}/api/v1/BulkDonorSchedule/upsert-schedules`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(),
          body: JSON.stringify(request),
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    },
  });
};
